// src/components/myAccount/pages/SubscriptionSuccessPage.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, FileText, CreditCard, Home } from 'lucide-react';
import stripeService from '../../../services/stripeService';
import { supabase } from '../../../config';

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    fetchSubscriptionDetails(sessionId);
  }, [searchParams]);

  const fetchSubscriptionDetails = async (sessionId) => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Please log in to view subscription details');
        setLoading(false);
        return;
      }

      // Get checkout session details
      const sessionResponse = await stripeService.getCheckoutSession(sessionId, user.id);
      
      if (sessionResponse?.success) {
        // Subscription might not be created yet, so check session metadata for plan info
        const sessionData = sessionResponse.session;
        const metadata = sessionData?.metadata || {};
        const planId = metadata.plan_id || metadata.planId;
        const planType = metadata.plan_type || metadata.planType;
        
        // Try to get subscription from response
        let subData = sessionResponse.subscription;
        
        // If no subscription yet, try to get from database
        if (!subData) {
          try {
            const { data: subscriptionData } = await supabase
              .from('subscriptions')
              .select('*')
              .eq('user_id', user.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .single();
            
            if (subscriptionData) {
              subData = {
                id: subscriptionData.stripe_subscription_id,
                status: subscriptionData.status,
                current_period_end: subscriptionData.current_period_end ? new Date(subscriptionData.current_period_end).getTime() / 1000 : null
              };
            }
          } catch (err) {
            console.log('No subscription found in database yet');
          }
        }
        
        if (subData) {
          setSubscription(subData);
        }

        // Get plan details from database
        if (planId) {
          const { data: planData } = await supabase
            .from('subscription_plans')
            .select('*')
            .eq('id', planId)
            .single();
          
          if (planData) {
            setPlan(planData);
          }
        } else if (planType) {
          // Fallback to plan_type
          const { data: planData } = await supabase
            .from('subscription_plans')
            .select('*')
            .eq('plan_type', planType)
            .single();
          
          if (planData) {
            setPlan(planData);
          }
        }
        
        // If we have plan info but no subscription yet, that's okay - show success anyway
        if (!subData && !planId && !planType) {
          setError('Could not retrieve subscription details. Your payment may still be processing.');
        }
      } else {
        setError('Could not retrieve subscription details');
      }
    } catch (err) {
      console.error('Error fetching subscription details:', err);
      setError(err.message || 'Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-600 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/my-account"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Home className="h-4 w-4 mr-2" />
            Go to My Account
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Success Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-8 py-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Subscription Activated!</h1>
            <p className="text-green-100 text-lg">Thank you for your subscription</p>
          </div>

          {/* Subscription Details */}
          <div className="p-8">
            {plan && (
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Plan Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Plan Name</p>
                    <p className="text-lg font-medium text-gray-900">{plan.name}</p>
                  </div>
                  {plan.description && (
                    <div>
                      <p className="text-sm text-gray-600">Description</p>
                      <p className="text-lg font-medium text-gray-900">{plan.description}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {subscription && (
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="text-lg font-medium text-green-600 capitalize">{subscription.status}</p>
                  </div>
                  {subscription.current_period_end && (
                    <div>
                      <p className="text-sm text-gray-600">Next Billing Date</p>
                      <p className="text-lg font-medium text-gray-900">
                        {formatDate(subscription.current_period_end)}
                      </p>
                    </div>
                  )}
                  {subscription.id && (
                    <div>
                      <p className="text-sm text-gray-600">Subscription ID</p>
                      <p className="text-sm font-mono text-gray-700 break-all">{subscription.id}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/my-account/billing-history"
                className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FileText className="h-5 w-5 mr-2" />
                View Billing History
              </Link>
              <Link
                to="/my-account/payment-methods"
                className="flex-1 flex items-center justify-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <CreditCard className="h-5 w-5 mr-2" />
                Manage Payment Methods
              </Link>
              <Link
                to="/my-account"
                className="flex-1 flex items-center justify-center px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Home className="h-5 w-5 mr-2" />
                Go to Dashboard
              </Link>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
          <p className="text-gray-600 mb-4">
            If you have any questions about your subscription, please don't hesitate to contact our support team.
          </p>
          <Link
            to="/my-account"
            className="inline-flex items-center text-blue-600 hover:text-blue-700"
          >
            Contact Support
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;

